import React, { useEffect } from 'react';
import { get, isEmpty } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import getMetricsData from '@src/api/metricContainer';
import axiosInstance from '@api/axiosInstance';
import { trackQueryError } from '@utils/sensor/apptracker';
import { formatTimeseriesData } from '../utils/format';
import useTimeseriesQuery from './useTimeseriesQuery';
import { DATA_TAGS, useGetData, useSetData } from './useData';
import { buildMetricFilterSeries } from '../utils/metricFilterSeriesOption';
import { findAxisIndex } from './utils';
import { TIME_AXIS, VALUE_AXIS } from '../utils/constants';

const defaultMetricValue = { total: null, data: [] };
const defaultResponseValue = { totals: {}, data: {} };

export function useFetchMetricFilterSeries({
  enabled = false,
  useBuildQuery = useTimeseriesQuery,
  dataTag = DATA_TAGS.metricFilter,
}) {
  const metricFilter = useSelector(metricFilterRulesSelector);
  const { query: baseQuery, metrics = [], interval } = useBuildQuery();
  const query = { ...baseQuery, metricFilter: metricFilter };
  const setMetricFilterData = useSetData({ tag: dataTag, defaultValue: defaultResponseValue });

  const {
    isFetching,
    isError,
    data: response,
  } = useQuery(
    ['timeseries-metric-filter', query],
    () => {
      return getMetricsData({ queries: [query] }, {}, axiosInstance).then((response) => {
        const { data = [], totals = {}, annotations = [] } = get(response, ['data', '0'], {});
        const formattedData = formatTimeseriesData({ metrics, data, interval, annotations });
        return { data: formattedData, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: 3,
      enabled: enabled && metricFilter.length > 0 && metrics.length > 0,
      onError: (err) => {
        trackQueryError({ type: 'metric-filter-series', err });
      },
    },
  );

  const formattedData = isEmpty(response) ? defaultResponseValue : response;

  // this state consumed by most components instead of the query hook for perfomance
  useEffect(() => {
    setMetricFilterData(formattedData);
  }, [formattedData, setMetricFilterData]);

  return { isFetching, isError, data: formattedData };
}

// hook to retrieve data
export const useMetricFilterData = ({ metric = null, dataTag }) => {
  const { data, totals }: any = useGetData({ tag: dataTag, defaultValue: defaultResponseValue });
  if (metric) {
    return data && totals && metric in data
      ? { data: data[metric], total: totals[metric] }
      : defaultMetricValue;
  }
  return { data, totals };
};

export function formatMetricFilterSeries(option, data = [], barChart = false) {
  // add or update metric filter series
  const metricFilterChartData = buildMetricFilterSeries(data, {
    xAxisIndex: findAxisIndex(option.xAxis, TIME_AXIS),
    yAxisIndex: findAxisIndex(option.yAxis, VALUE_AXIS),
    barChart,
  });

  return { series: [metricFilterChartData] };
}

function useMetricFilterSeries({
  metric,
  instance,
  additionalTriggerDeps = [],
  dataTag = DATA_TAGS.metricFilter,
  barChart = false,
}: any) {
  const data = useMetricFilterData({ metric, dataTag });

  useEffect(() => {
    if (instance) {
      const option = instance.getOption();
      if (option && option._viewType === 'timeseries') {
        // const total = totals[selectedMetric];
        const { data: chartData = [] } = data;
        const { series } = formatMetricFilterSeries(option, chartData, barChart);
        instance.setOption({ series });
      }
    }
  }, [instance, metric, data, barChart, ...additionalTriggerDeps]);
}

export default useMetricFilterSeries;
